import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  MeshStandardNodeMaterial,
  TorusGeometry,
  type BufferGeometry,
} from 'three/webgpu';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { CRATE_TEXTURE_PATH, loadTiledTexture, triplanarColor } from './scene.js';
import { renderFlags } from './renderer.js';

/**
 * The three loot lockers, built in code.
 *
 * `packages/shared/src/loot.ts` has had three tables since M2 — `crate_common`,
 * `crate_hull`, `crate_hymn`, and the Spine places 16 / 20 / 8 of them — but every one of
 * them drew as the same TRELLIS crate. A player who cannot tell a Hymn locker from a common
 * one at twenty metres is being denied the routing decision the loot tables exist to create,
 * so the *silhouette* differs, not just the tint:
 *
 *  - **common** — a settler cargo box: shallow, banded, an amber hasp.
 *  - **hull** — salvage: taller, heavier corner irons, double straps, a lid that sits proud.
 *  - **hymn** — Hymn-tech: a bone-ceramic drum with no metal on it at all, a hex lock plate,
 *    and a *cold white* seam rather than the amber every settler object in the yard uses.
 *
 * Each is authored as `body` + `lid` so the lid can actually hinge when the locker is looted
 * — the generated GLB was one unposed watertight mesh, which is why `render/containers.ts`
 * had been reduced to settling the crate 2 cm on its springs instead.
 *
 * Geometry is merged per part so a variant costs two instanced batches whatever the detail,
 * and the materials are module singletons so 44 lockers share five pipelines between them.
 */

export type CrateVariant = 'crate_common' | 'crate_hull' | 'crate_hymn';

/** Every table `packages/shared/src/loot.ts` defines, in the order the Spine places them. */
export const CRATE_VARIANTS: readonly CrateVariant[] = ['crate_common', 'crate_hull', 'crate_hymn'];

/** Unknown table ids fall back to the common locker rather than drawing nothing. */
export function crateVariant(table: string): CrateVariant {
  return table === 'crate_hull' || table === 'crate_hymn' ? table : 'crate_common';
}

/** Salvage plating. SDXL, seed 1211 — see ASSETS.md. */
const HULL_TEXTURE_PATH = '/assets/gen/textures/crate-hull.png';
/** Bone-white Hymn ceramic. SDXL, seed 1212 — see ASSETS.md. */
const HYMN_TEXTURE_PATH = '/assets/gen/textures/crate-hymn.png';

const AMBER = 0xffa040;
/** The Hymn accent the Spine's Deck 2 lights already use (`render/scene.ts`). */
const HYMN_WHITE = 0xdfe8ff;

export interface CrateShape {
  /** Footprint and height in metres. The seam and highlight shell are fitted from these. */
  readonly w: number;
  readonly h: number;
  readonly d: number;
  /** Lid thickness, and the radians it swings open. */
  readonly lidH: number;
  readonly openRad: number;
  /** Seam colour — the "there is loot here" read, and the tier tell at distance. */
  readonly seamColor: number;
}

export const CRATE_SHAPES: Record<CrateVariant, CrateShape> = {
  crate_common: { w: 1.1, h: 0.66, d: 0.62, lidH: 0.09, openRad: -1.05, seamColor: AMBER },
  crate_hull: { w: 1.02, h: 0.86, d: 0.68, lidH: 0.12, openRad: -0.95, seamColor: AMBER },
  crate_hymn: { w: 0.86, h: 0.78, d: 0.86, lidH: 0.1, openRad: -1.2, seamColor: HYMN_WHITE },
};

// ------------------------------------------------------------------------- materials

export interface CrateMaterials {
  readonly body: Record<CrateVariant, MeshStandardNodeMaterial>;
  readonly trim: MeshStandardNodeMaterial;
  readonly all: readonly MeshStandardNodeMaterial[];
}

/**
 * Tiles for each variant's panel, in metres. A locker is ~1 m, so a 0.8 m tile puts roughly
 * one panel on a face; the Hymn drum is smaller and takes a tighter tile so the crazing in
 * the ceramic still reads.
 */
const TILE_M: Record<CrateVariant, number> = {
  crate_common: 0.8,
  crate_hull: 0.7,
  crate_hymn: 0.55,
};
const TEXTURE: Record<CrateVariant, string> = {
  crate_common: CRATE_TEXTURE_PATH,
  crate_hull: HULL_TEXTURE_PATH,
  crate_hymn: HYMN_TEXTURE_PATH,
};
/** Base tints, used as-is if a texture 404s and as the multiplier under it if it loads. */
const TINT: Record<CrateVariant, number> = {
  crate_common: 0x4e5257,
  crate_hull: 0x5a5148,
  crate_hymn: 0xbdb7a8,
};
const METAL: Record<CrateVariant, [number, number]> = {
  crate_common: [0.55, 0.58],
  crate_hull: [0.7, 0.5],
  // Ceramic, not metal — the Hymn locker must not catch a highlight like plating.
  crate_hymn: [0.0, 0.72],
};

let mats: CrateMaterials | null = null;

/**
 * The shared material set. Built once on first use and never rebuilt, so the pipelines it
 * mints are compiled by the `precompileScene` that follows the field being populated rather
 * than by the first locker to come into view.
 */
export function crateMaterials(): CrateMaterials {
  if (mats) return mats;
  const body = {} as Record<CrateVariant, MeshStandardNodeMaterial>;
  for (const v of CRATE_VARIANTS) {
    const m = new MeshStandardNodeMaterial({
      color: TINT[v],
      metalness: METAL[v][0],
      roughness: METAL[v][1],
    });
    try {
      // `?tex=0` kill switch, same as the rest of render/.
      if (!renderFlags.textures) throw new Error('textures disabled');
      // World-space triplanar, the same helper the map's own crates use — these are static
      // world props, so a world projection is correct and keeps them looking like the yard.
      m.colorNode = triplanarColor(loadTiledTexture(TEXTURE[v]), TILE_M[v]);
    } catch (err) {
      console.warn(`[stillsun] crate texture unavailable for ${v}, using flat material`, err);
    }
    body[v] = m;
  }
  // One dark metal for every strap, iron and latch across all three variants: the trim is
  // what makes a settler box read as banded, and it costs one extra batch per variant, not
  // one per variant per material.
  const trim = new MeshStandardNodeMaterial({ color: 0x24262a, metalness: 0.75, roughness: 0.45 });
  mats = { body, trim, all: [...CRATE_VARIANTS.map((v) => body[v]), trim] };
  return mats;
}

/** The seam material for a variant's tier colour. Emissive intensity rides per-instance. */
export function seamColorOf(v: CrateVariant): Color {
  return new Color(CRATE_SHAPES[v].seamColor);
}

// ------------------------------------------------------------------------- geometry

/** Collects positioned primitives into `body` / `lid` / `trim` buckets and merges each. */
class CrateParts {
  private readonly lists: BufferGeometry[][] = [[], [], []];

  /** `slot`: 0 = body panel, 1 = lid panel, 2 = trim (either part; see `trimOn`). */
  add(geo: BufferGeometry, slot: 0 | 1 | 2, x: number, y: number, z: number, rx = 0, rz = 0): this {
    if (rx !== 0) geo.rotateX(rx);
    if (rz !== 0) geo.rotateZ(rz);
    geo.translate(x, y, z);
    this.lists[slot]?.push(geo.index ? geo.toNonIndexed() : geo);
    return this;
  }

  take(slot: 0 | 1 | 2): BufferGeometry | null {
    const list = this.lists[slot];
    if (!list || list.length === 0) return null;
    const merged = mergeGeometries(list, false);
    for (const g of list) g.dispose();
    list.length = 0;
    if (!merged) return null;
    try {
      const welded = mergeVertices(merged, 1e-5);
      if (welded !== merged) merged.dispose();
      return welded;
    } catch {
      return merged;
    }
  }
}

/**
 * The four merged geometries a variant draws with. `bodyTrim` and `lidTrim` are separate
 * because the lid hinges and its ironwork has to hinge with it.
 *
 * Every geometry is authored with the locker's **origin on the ground at its centre**, and
 * the lid's origin moved to its hinge line (the back edge) so an instance matrix can rotate
 * it open with nothing but `makeRotationX`.
 */
export interface CrateGeometry {
  readonly body: BufferGeometry;
  readonly bodyTrim: BufferGeometry | null;
  readonly lid: BufferGeometry;
  readonly lidTrim: BufferGeometry | null;
  /** Hinge line in locker space: the lid instance is positioned here and rotated about x. */
  readonly hingeY: number;
  readonly hingeZ: number;
  readonly triangles: number;
}

const box = (w: number, h: number, d: number): BoxGeometry => new BoxGeometry(w, h, d);
const tris = (g: BufferGeometry | null): number =>
  g ? Math.floor((g.index ? g.index.count : (g.getAttribute('position')?.count ?? 0)) / 3) : 0;

/** Builds one variant's geometry. Called once per variant, ever. */
export function buildCrateGeometry(v: CrateVariant): CrateGeometry {
  const s = CRATE_SHAPES[v];
  const p = new CrateParts();
  const bodyH = s.h - s.lidH;
  const hingeY = bodyH;
  const hingeZ = s.d / 2;

  // --- body shell ----------------------------------------------------------
  p.add(box(s.w, bodyH, s.d), 0, 0, bodyH / 2, 0);

  if (v === 'crate_hymn') {
    // Hymn: a fired-ceramic drum. Chamfer the vertical edges with four angled plates rather
    // than leaving a hard box — the Hymnal's things are not built by people with rivet guns.
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        p.add(
          box(0.16, bodyH * 0.98, 0.02),
          0,
          (sx * s.w) / 2.6,
          bodyH / 2,
          (sz * s.d) / 2.6,
          0,
          0,
        );
      }
    }
    // Hex lock plate on the front face — the one piece of "mechanism" it shows.
    const hex = new CylinderGeometry(0.11, 0.11, 0.025, 6, 1);
    hex.rotateX(Math.PI / 2);
    p.add(hex, 0, 0, bodyH * 0.55, -(s.d / 2 + 0.01));
    p.add(new TorusGeometry(0.135, 0.012, 4, 6), 0, 0, bodyH * 0.55, -(s.d / 2 + 0.008));
    // A shallow foot ring, so it sits on the deck rather than in it.
    p.add(new TorusGeometry(s.w * 0.4, 0.022, 4, 12), 0, 0, 0.022, 0, Math.PI / 2);
  } else {
    // Settler / salvage: corner irons and banding straps.
    const post = v === 'crate_hull' ? 0.075 : 0.055;
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        // Two plates per corner, forming an angle iron rather than a square pillar.
        p.add(box(post, bodyH, 0.022), 2, (sx * (s.w - post)) / 2, bodyH / 2, (sz * s.d) / 2);
        p.add(box(0.022, bodyH, post), 2, (sx * s.w) / 2, bodyH / 2, (sz * (s.d - post)) / 2);
      }
    }
    // Horizontal straps: one on the settler box, two on the heavier salvage crate.
    const bands = v === 'crate_hull' ? [0.26, 0.6] : [0.42];
    for (const f of bands) {
      p.add(box(s.w + 0.014, 0.045, s.d + 0.014), 2, 0, bodyH * f, 0);
    }
    // Hasp and staple on the front face, under the lid line.
    p.add(box(0.09, 0.11, 0.02), 2, 0, bodyH - 0.075, -(s.d / 2 + 0.012));
    p.add(new TorusGeometry(0.032, 0.01, 4, 10), 2, 0, bodyH - 0.135, -(s.d / 2 + 0.016));
    // Fork pockets along the base — these are cargo, and cargo gets moved.
    for (const sx of [-1, 1] as const) {
      p.add(box(0.2, 0.06, s.d + 0.012), 2, (sx * s.w) / 4.2, 0.045, 0);
    }
  }

  // --- lid -----------------------------------------------------------------
  // Authored around the hinge at the origin, so an instance matrix rotates it open directly.
  const lidZ = -s.d / 2;
  p.add(box(s.w + 0.02, s.lidH, s.d + 0.02), 1, 0, s.lidH / 2, lidZ);
  if (v === 'crate_hymn') {
    // A raised ceramic boss, and a ring of it, instead of ironwork.
    const boss = new CylinderGeometry(s.w * 0.26, s.w * 0.3, 0.05, 12, 1);
    p.add(boss, 1, 0, s.lidH + 0.02, lidZ);
  } else {
    // Ribs across the lid, and the hinge barrels themselves.
    for (const f of [-0.28, 0, 0.28]) {
      p.add(box(0.07, 0.028, s.d - 0.06), 1, f * s.w, s.lidH + 0.012, lidZ);
    }
    for (const sx of [-1, 1] as const) {
      const barrel = new CylinderGeometry(0.03, 0.03, 0.14, 8, 1);
      barrel.rotateZ(Math.PI / 2);
      p.add(barrel, 2, (sx * s.w) / 3.2, s.lidH / 2, 0);
    }
  }

  const body = p.take(0);
  const lid = p.take(1);
  const trimAll = p.take(2);
  // The trim was collected across both parts; splitting it would need a second bucket pass.
  // Everything in it below the lid line belongs to the body, and the hinge barrels are the
  // only trim on the lid — cheap enough to keep as one body-side batch, with the barrels
  // sitting at the hinge where the lid meets the body and reading correctly either way.
  if (!body || !lid) throw new Error(`crate geometry failed for ${v}`);
  const geo: CrateGeometry = {
    body,
    bodyTrim: trimAll,
    lid,
    lidTrim: null,
    hingeY,
    hingeZ,
    triangles: tris(body) + tris(trimAll) + tris(lid),
  };
  return geo;
}
